<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import SettingsSection from '../../../../../components/SettingsSection.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Spinner from 'shared/components/Spinner.vue';
import WahaAPI from 'dashboard/api/waha';

const STATUS_LABELS = {
  STARTING: 'STARTING',
  SCAN_QR_CODE: 'SCAN_QR_CODE',
  WORKING: 'WORKING',
  STOPPED: 'STOPPED',
  FAILED: 'FAILED',
};

export default {
  components: {
    SettingsSection,
    NextButton,
    Spinner,
  },
  props: {
    inbox: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      isLoading: false,
      isActionLoading: false,
      sessionStatus: null,
      qrCode: null,
      phoneNumber: null,
      sessionName: null,
      chatwootApp: { configured: false, error: null },
      isReconfiguringApp: false,
      error: null,
      qrRefreshInterval: null,
      showPairingCode: false,
      pairingPhoneNumber: '',
      pairingCode: '',
      pairingError: '',
      isLoadingPairingCode: false,
      // Settings
      isLoadingSettings: false,
      // Chatwoot App Settings
      chatwootAppSettings: {
        conversation_mode: 'single',
        mark_messages_read: true,
        language: 'pt-br',
        add_agent_name: false,
        link_preview: true,
        skip_chats: { groups: false, status: false, channels: false },
        commands: { server: true, queue: true },
      },
      isSavingChatwootApp: false,
      // Session Settings
      sessionSettings: {
        presence_auto_online: true,
        ignore: { groups: true, status: true, channels: true, broadcast: true },
        proxy: { enabled: false, server: '', username: '', password: '' },
        noweb_store: { enabled: false, full_sync: false },
      },
      isSavingSession: false,
      // Quick command loading states
      isPullingMessages: false,
      isPullingContacts: false,
      isPullingChats: false,
    };
  },
  computed: {
    ...mapGetters({
      accountId: 'getCurrentAccountId',
    }),
    inboxId() {
      return this.inbox.id;
    },
    normalizedStatus() {
      return (this.sessionStatus || '').toUpperCase();
    },
    statusClass() {
      const statusMap = {
        WORKING:
          'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
        SCAN_QR_CODE:
          'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
        STARTING:
          'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
        STOPPED:
          'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
        FAILED: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
      };
      return statusMap[this.normalizedStatus] || statusMap.STOPPED;
    },
    statusLabel() {
      const status = (this.sessionStatus || 'UNKNOWN').toUpperCase();
      return this.$t(`INBOX_MGMT.WAHA.STATUS.${status}`);
    },
    showQrCode() {
      return (
        this.normalizedStatus === STATUS_LABELS.SCAN_QR_CODE && this.qrCode
      );
    },
    isConnected() {
      return this.normalizedStatus === STATUS_LABELS.WORKING;
    },
    isStopped() {
      return this.normalizedStatus === STATUS_LABELS.STOPPED;
    },
    canStart() {
      return this.isStopped || this.normalizedStatus === STATUS_LABELS.FAILED;
    },
    canStop() {
      return (
        this.isConnected || this.normalizedStatus === STATUS_LABELS.SCAN_QR_CODE
      );
    },
    isChatwootAppConfigured() {
      return Boolean(this.chatwootApp?.configured);
    },
    chatwootAppErrorReason() {
      const reason = this.chatwootApp?.error?.reason;
      if (!reason) return '';
      const key = `INBOX_MGMT.WAHA.CHATWOOT_APP_STATUS.REASONS.${reason}`;
      const translated = this.$t(key);
      // Fall back to the generic reason when an unknown code arrives.
      return translated === key
        ? this.$t(
            'INBOX_MGMT.WAHA.CHATWOOT_APP_STATUS.REASONS.configuration_failed'
          )
        : translated;
    },
  },
  mounted() {
    this.fetchStatus();
    this.fetchSettings();
  },
  beforeUnmount() {
    this.clearQrRefreshInterval();
  },
  methods: {
    async fetchStatus() {
      this.isLoading = true;
      this.error = null;
      try {
        const response = await WahaAPI.getStatus(this.accountId, this.inboxId);
        this.updateStatusFromResponse(response.data);
        this.setupQrRefreshIfNeeded();
      } catch (err) {
        this.error =
          err.message || this.$t('INBOX_MGMT.WAHA.ERRORS.FETCH_STATUS');
      } finally {
        this.isLoading = false;
      }
    },
    updateStatusFromResponse(data) {
      this.sessionStatus = data.status;
      this.qrCode = data.qr_code;
      this.phoneNumber = data.phone_number;
      this.sessionName = data.session_name;
      if (data.chatwoot_app) {
        this.chatwootApp = data.chatwoot_app;
      }
    },
    setupQrRefreshIfNeeded() {
      this.clearQrRefreshInterval();
      if (this.normalizedStatus === STATUS_LABELS.SCAN_QR_CODE) {
        this.qrRefreshInterval = setInterval(() => {
          this.fetchStatus();
        }, 15000); // Refresh every 15 seconds
      }
    },
    clearQrRefreshInterval() {
      if (this.qrRefreshInterval) {
        clearInterval(this.qrRefreshInterval);
        this.qrRefreshInterval = null;
      }
    },
    async fetchSettings() {
      this.isLoadingSettings = true;
      try {
        const response = await WahaAPI.getSettings(
          this.accountId,
          this.inboxId
        );
        const data = response.data;
        if (data.chatwoot_app_settings) {
          this.chatwootAppSettings = {
            ...this.chatwootAppSettings,
            ...data.chatwoot_app_settings,
          };
        }
        if (data.session_settings) {
          this.sessionSettings = {
            ...this.sessionSettings,
            ...data.session_settings,
            proxy: {
              ...this.sessionSettings.proxy,
              ...(data.session_settings.proxy || {}),
            },
            ignore: {
              ...this.sessionSettings.ignore,
              ...(data.session_settings.ignore || {}),
            },
            noweb_store: {
              ...this.sessionSettings.noweb_store,
              ...(data.session_settings.noweb_store || {}),
            },
          };
        }
      } catch {
        // silently ignore settings load errors
      } finally {
        this.isLoadingSettings = false;
      }
    },
    async refreshQrCode() {
      this.isActionLoading = true;
      try {
        await WahaAPI.refreshQr(this.accountId, this.inboxId);
        await this.fetchStatus();
        useAlert(this.$t('INBOX_MGMT.WAHA.ALERTS.QR_REFRESHED'));
      } catch (err) {
        useAlert(err.message || this.$t('INBOX_MGMT.WAHA.ERRORS.REFRESH_QR'));
      } finally {
        this.isActionLoading = false;
      }
    },
    async startSession() {
      this.isActionLoading = true;
      try {
        await WahaAPI.start(this.accountId, this.inboxId);
        useAlert(this.$t('INBOX_MGMT.WAHA.ALERTS.SESSION_STARTED'));
        await this.fetchStatus();
      } catch (err) {
        useAlert(
          err.message || this.$t('INBOX_MGMT.WAHA.ERRORS.START_SESSION')
        );
      } finally {
        this.isActionLoading = false;
      }
    },
    async stopSession() {
      this.isActionLoading = true;
      try {
        await WahaAPI.stop(this.accountId, this.inboxId);
        useAlert(this.$t('INBOX_MGMT.WAHA.ALERTS.SESSION_STOPPED'));
        await this.fetchStatus();
      } catch (err) {
        useAlert(err.message || this.$t('INBOX_MGMT.WAHA.ERRORS.STOP_SESSION'));
      } finally {
        this.isActionLoading = false;
      }
    },
    async reconnectSession() {
      this.isActionLoading = true;
      try {
        await WahaAPI.reconnect(this.accountId, this.inboxId);
        useAlert(this.$t('INBOX_MGMT.WAHA.ALERTS.SESSION_RECONNECTED'));
        await this.fetchStatus();
      } catch (err) {
        useAlert(
          err.message || this.$t('INBOX_MGMT.WAHA.ERRORS.RECONNECT_SESSION')
        );
      } finally {
        this.isActionLoading = false;
      }
    },
    async disconnectSession() {
      if (!window.confirm(this.$t('INBOX_MGMT.WAHA.CONFIRM_DISCONNECT'))) {
        return;
      }
      this.isActionLoading = true;
      try {
        await WahaAPI.disconnect(this.accountId, this.inboxId);
        useAlert(this.$t('INBOX_MGMT.WAHA.ALERTS.SESSION_DISCONNECTED'));
        await this.fetchStatus();
      } catch (err) {
        useAlert(
          err.message || this.$t('INBOX_MGMT.WAHA.ERRORS.DISCONNECT_SESSION')
        );
      } finally {
        this.isActionLoading = false;
      }
    },
    async reconfigureApp() {
      this.isReconfiguringApp = true;
      try {
        const { data } = await WahaAPI.reconfigureApp(
          this.accountId,
          this.inboxId
        );
        if (data.chatwoot_app) {
          this.chatwootApp = data.chatwoot_app;
        }
        useAlert(
          this.$t('INBOX_MGMT.WAHA.CHATWOOT_APP_STATUS.RECONFIGURE_SUCCESS')
        );
        await this.fetchStatus();
      } catch (err) {
        // The 422 response still carries the updated chatwoot_app error state.
        const appState = err.response?.data?.chatwoot_app;
        if (appState) {
          this.chatwootApp = appState;
        }
        useAlert(
          this.$t('INBOX_MGMT.WAHA.CHATWOOT_APP_STATUS.RECONFIGURE_ERROR')
        );
      } finally {
        this.isReconfiguringApp = false;
      }
    },
    async requestPairingCode() {
      if (!this.pairingPhoneNumber) return;
      this.isLoadingPairingCode = true;
      this.pairingError = '';
      this.pairingCode = '';
      try {
        const { data } = await WahaAPI.requestPairingCode(
          this.accountId,
          this.inboxId,
          this.pairingPhoneNumber
        );
        this.pairingCode = data.code;
      } catch {
        this.pairingError = this.$t('INBOX_MGMT.WAHA.PAIRING_CODE.ERROR');
      } finally {
        this.isLoadingPairingCode = false;
      }
    },
    async saveChatwootAppSettings() {
      this.isSavingChatwootApp = true;
      try {
        await WahaAPI.updateChatwootAppSettings(
          this.accountId,
          this.inboxId,
          this.chatwootAppSettings
        );
        useAlert(this.$t('INBOX_MGMT.WAHA.CHATWOOT_APP_SETTINGS.SAVED'));
      } catch {
        useAlert(this.$t('INBOX_MGMT.WAHA.CHATWOOT_APP_SETTINGS.SAVE_ERROR'));
      } finally {
        this.isSavingChatwootApp = false;
      }
    },
    async saveSessionSettings() {
      this.isSavingSession = true;
      try {
        await WahaAPI.updateSessionSettings(
          this.accountId,
          this.inboxId,
          this.sessionSettings
        );
        useAlert(this.$t('INBOX_MGMT.WAHA.SESSION_SETTINGS.SAVED'));
        // proxy/noweb changes restart the session on WAHA — refresh status.
        await this.fetchStatus();
      } catch {
        useAlert(this.$t('INBOX_MGMT.WAHA.SESSION_SETTINGS.SAVE_ERROR'));
      } finally {
        this.isSavingSession = false;
      }
    },
    async pullMessages() {
      this.isPullingMessages = true;
      try {
        const { data } = await WahaAPI.pullMessages(
          this.accountId,
          this.inboxId
        );
        useAlert(
          this.$t('INBOX_MGMT.WAHA.QUICK_COMMANDS.PULL_MESSAGES.SUCCESS', {
            count: data.count,
          })
        );
      } catch {
        useAlert(this.$t('INBOX_MGMT.WAHA.QUICK_COMMANDS.PULL_MESSAGES.ERROR'));
      } finally {
        this.isPullingMessages = false;
      }
    },
    async pullContacts() {
      this.isPullingContacts = true;
      try {
        const { data } = await WahaAPI.pullContacts(
          this.accountId,
          this.inboxId
        );
        useAlert(
          this.$t('INBOX_MGMT.WAHA.QUICK_COMMANDS.PULL_CONTACTS.SUCCESS', {
            count: data.count,
          })
        );
      } catch {
        useAlert(this.$t('INBOX_MGMT.WAHA.QUICK_COMMANDS.PULL_CONTACTS.ERROR'));
      } finally {
        this.isPullingContacts = false;
      }
    },
    async pullChats() {
      this.isPullingChats = true;
      try {
        const { data } = await WahaAPI.pullChats(this.accountId, this.inboxId);
        useAlert(
          this.$t('INBOX_MGMT.WAHA.QUICK_COMMANDS.PULL_CHATS.SUCCESS', {
            count: data.count,
          })
        );
      } catch {
        useAlert(this.$t('INBOX_MGMT.WAHA.QUICK_COMMANDS.PULL_CHATS.ERROR'));
      } finally {
        this.isPullingChats = false;
      }
    },
  },
};
</script>

<template>
  <div class="mx-8">
    <!-- Loading State -->
    <div v-if="isLoading" class="flex items-center justify-center py-12">
      <Spinner size="" />
    </div>

    <!-- Error State -->
    <div
      v-else-if="error"
      class="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg mb-4"
    >
      <p class="text-sm text-red-800 dark:text-red-200 mb-2">{{ error }}</p>
      <NextButton size="sm" @click="fetchStatus">
        {{ $t('INBOX_MGMT.WAHA.RETRY') }}
      </NextButton>
    </div>

    <!-- Main Content -->
    <template v-else>
      <!-- Session Status -->
      <SettingsSection
        :title="$t('INBOX_MGMT.WAHA.SESSION_STATUS.TITLE')"
        :sub-title="$t('INBOX_MGMT.WAHA.SESSION_STATUS.SUBTITLE')"
      >
        <div class="flex items-center gap-4 mb-4">
          <span
            class="px-3 py-1 rounded-full text-sm font-medium"
            :class="statusClass"
          >
            {{ statusLabel }}
          </span>
          <NextButton
            size="sm"
            variant="outline"
            :is-loading="isActionLoading"
            @click="fetchStatus"
          >
            {{ $t('INBOX_MGMT.WAHA.REFRESH_STATUS') }}
          </NextButton>
        </div>

        <div v-if="phoneNumber" class="text-sm text-n-slate-11 mb-2">
          <span class="font-medium"
            >{{ $t('INBOX_MGMT.WAHA.PHONE_NUMBER') }}:</span
          >
          {{ phoneNumber }}
        </div>

        <div v-if="sessionName" class="text-sm text-n-slate-11">
          <span class="font-medium"
            >{{ $t('INBOX_MGMT.WAHA.SESSION_NAME') }}:</span
          >
          {{ sessionName }}
        </div>
      </SettingsSection>

      <!-- QR Code Section -->
      <SettingsSection
        v-if="showQrCode"
        :title="$t('INBOX_MGMT.WAHA.QR_CODE.TITLE')"
        :sub-title="$t('INBOX_MGMT.WAHA.QR_CODE.SUBTITLE')"
      >
        <div class="flex flex-col items-center gap-4">
          <div class="bg-white p-4 rounded-lg shadow-sm">
            <img
              :src="qrCode"
              alt="WhatsApp QR Code"
              class="w-64 h-64 object-contain"
            />
          </div>
          <p class="text-sm text-n-slate-11 text-center max-w-md">
            {{ $t('INBOX_MGMT.WAHA.QR_CODE.INSTRUCTIONS') }}
          </p>
          <NextButton
            size="sm"
            variant="outline"
            :is-loading="isActionLoading"
            @click="refreshQrCode"
          >
            {{ $t('INBOX_MGMT.WAHA.QR_CODE.REFRESH') }}
          </NextButton>

          <!-- Pairing Code Toggle -->
          <button
            class="mt-2 text-sm text-woot-500 hover:text-woot-600 underline cursor-pointer bg-transparent border-0"
            @click="showPairingCode = !showPairingCode"
          >
            {{
              showPairingCode
                ? $t('INBOX_MGMT.WAHA.PAIRING_CODE.TOGGLE_HIDE')
                : $t('INBOX_MGMT.WAHA.PAIRING_CODE.TOGGLE_SHOW')
            }}
          </button>

          <!-- Pairing Code Form -->
          <div v-if="showPairingCode" class="w-full max-w-sm space-y-3 mt-1">
            <input
              v-model="pairingPhoneNumber"
              type="tel"
              :placeholder="
                $t('INBOX_MGMT.WAHA.PAIRING_CODE.PHONE_PLACEHOLDER')
              "
              class="w-full px-3 py-2 text-sm border border-n-weak rounded-lg focus:outline-none focus:ring-2 focus:ring-woot-500 bg-white dark:bg-n-solid-1 text-n-slate-12"
            />
            <button
              :disabled="isLoadingPairingCode || !pairingPhoneNumber"
              class="w-full px-4 py-2 text-sm font-medium text-white bg-woot-500 rounded-lg hover:bg-woot-600 disabled:opacity-50 disabled:cursor-not-allowed"
              @click="requestPairingCode"
            >
              {{
                isLoadingPairingCode
                  ? $t('INBOX_MGMT.WAHA.PAIRING_CODE.GETTING_CODE')
                  : $t('INBOX_MGMT.WAHA.PAIRING_CODE.GET_CODE_BUTTON')
              }}
            </button>

            <div
              v-if="pairingCode"
              class="p-4 bg-n-alpha-1 rounded-lg text-center border border-n-weak"
            >
              <p class="text-xs text-n-slate-11 mb-1">
                {{ $t('INBOX_MGMT.WAHA.PAIRING_CODE.RESULT_INSTRUCTIONS') }}
              </p>
              <p
                class="text-2xl font-mono font-bold text-n-slate-12 tracking-widest"
              >
                {{ pairingCode }}
              </p>
              <p class="text-xs text-n-slate-10 mt-2">
                {{ $t('INBOX_MGMT.WAHA.PAIRING_CODE.RESULT_HINT') }}
              </p>
            </div>

            <p v-if="pairingError" class="text-sm text-red-500">
              {{ pairingError }}
            </p>
          </div>
        </div>
      </SettingsSection>

      <!-- Session Actions -->
      <SettingsSection
        :title="$t('INBOX_MGMT.WAHA.ACTIONS.TITLE')"
        :sub-title="$t('INBOX_MGMT.WAHA.ACTIONS.SUBTITLE')"
      >
        <div class="flex flex-wrap gap-3">
          <NextButton
            v-if="canStart"
            :is-loading="isActionLoading"
            @click="startSession"
          >
            {{ $t('INBOX_MGMT.WAHA.ACTIONS.START') }}
          </NextButton>

          <NextButton
            v-if="canStop"
            variant="outline"
            :is-loading="isActionLoading"
            @click="stopSession"
          >
            {{ $t('INBOX_MGMT.WAHA.ACTIONS.STOP') }}
          </NextButton>

          <NextButton
            v-if="isConnected"
            variant="outline"
            :is-loading="isActionLoading"
            @click="reconnectSession"
          >
            {{ $t('INBOX_MGMT.WAHA.ACTIONS.RECONNECT') }}
          </NextButton>

          <NextButton
            v-if="isConnected"
            variant="outline"
            color="ruby"
            :is-loading="isActionLoading"
            @click="disconnectSession"
          >
            {{ $t('INBOX_MGMT.WAHA.ACTIONS.DISCONNECT') }}
          </NextButton>
        </div>
      </SettingsSection>

      <!-- WAHA Chatwoot App Status -->
      <SettingsSection
        :title="$t('INBOX_MGMT.WAHA.CHATWOOT_APP_STATUS.TITLE')"
        :sub-title="$t('INBOX_MGMT.WAHA.CHATWOOT_APP_STATUS.SUBTITLE')"
      >
        <div
          v-if="isChatwootAppConfigured"
          class="flex items-start gap-2 p-3 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800"
        >
          <fluent-icon
            icon="checkmark-circle"
            size="18"
            class="text-green-600 dark:text-green-400 shrink-0 mt-0.5"
          />
          <p class="text-sm text-green-800 dark:text-green-200">
            {{ $t('INBOX_MGMT.WAHA.CHATWOOT_APP_STATUS.CONFIGURED') }}
          </p>
        </div>

        <div
          v-else
          class="p-3 rounded-lg bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800"
        >
          <div class="flex items-start gap-2">
            <fluent-icon
              icon="warning"
              size="18"
              class="text-yellow-600 dark:text-yellow-400 shrink-0 mt-0.5"
            />
            <div class="flex-1">
              <p class="text-sm text-yellow-800 dark:text-yellow-200">
                {{ $t('INBOX_MGMT.WAHA.CHATWOOT_APP_STATUS.NOT_CONFIGURED') }}
              </p>
              <p
                v-if="chatwootAppErrorReason"
                class="mt-2 text-xs text-yellow-700 dark:text-yellow-300"
              >
                <span class="font-medium"
                  >{{
                    $t('INBOX_MGMT.WAHA.CHATWOOT_APP_STATUS.REASON_LABEL')
                  }}:</span
                >
                {{ chatwootAppErrorReason }}
              </p>
            </div>
          </div>
          <NextButton
            size="sm"
            class="mt-3"
            :is-loading="isReconfiguringApp"
            :disabled="isReconfiguringApp"
            :label="
              isReconfiguringApp
                ? $t('INBOX_MGMT.WAHA.CHATWOOT_APP_STATUS.RECONFIGURING')
                : $t('INBOX_MGMT.WAHA.CHATWOOT_APP_STATUS.RECONFIGURE')
            "
            @click="reconfigureApp"
          />
        </div>
      </SettingsSection>

      <!-- Quick Commands -->
      <SettingsSection
        :title="$t('INBOX_MGMT.WAHA.QUICK_COMMANDS.TITLE')"
        :sub-title="$t('INBOX_MGMT.WAHA.QUICK_COMMANDS.SUBTITLE')"
      >
        <div class="flex flex-wrap gap-3">
          <NextButton
            variant="outline"
            :is-loading="isPullingMessages"
            :disabled="!isConnected"
            @click="pullMessages"
          >
            {{ $t('INBOX_MGMT.WAHA.QUICK_COMMANDS.PULL_MESSAGES.BUTTON') }}
          </NextButton>
          <NextButton
            variant="outline"
            :is-loading="isPullingContacts"
            :disabled="!isConnected"
            @click="pullContacts"
          >
            {{ $t('INBOX_MGMT.WAHA.QUICK_COMMANDS.PULL_CONTACTS.BUTTON') }}
          </NextButton>
          <NextButton
            variant="outline"
            :is-loading="isPullingChats"
            :disabled="!isConnected"
            @click="pullChats"
          >
            {{ $t('INBOX_MGMT.WAHA.QUICK_COMMANDS.PULL_CHATS.BUTTON') }}
          </NextButton>
        </div>
        <p v-if="!isConnected" class="mt-2 text-xs text-n-slate-10">
          {{ $t('INBOX_MGMT.WAHA.QUICK_COMMANDS.REQUIRES_CONNECTION') }}
        </p>
      </SettingsSection>

      <!-- Chatwoot App Settings -->
      <SettingsSection
        :title="$t('INBOX_MGMT.WAHA.CHATWOOT_APP_SETTINGS.TITLE')"
        :sub-title="$t('INBOX_MGMT.WAHA.CHATWOOT_APP_SETTINGS.SUBTITLE')"
      >
        <div class="space-y-4">
          <!-- Conversation Mode -->
          <div>
            <label class="block text-sm font-medium text-n-slate-12 mb-1">
              {{
                $t(
                  'INBOX_MGMT.WAHA.CHATWOOT_APP_SETTINGS.CONVERSATION_MODE.LABEL'
                )
              }}
            </label>
            <select
              v-model="chatwootAppSettings.conversation_mode"
              class="w-full max-w-xs px-3 !pr-9 bg-[right_0.65rem_center] py-2 text-sm border border-n-weak rounded-lg bg-white dark:bg-n-solid-1 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-woot-500"
            >
              <option value="single">
                {{
                  $t(
                    'INBOX_MGMT.WAHA.CHATWOOT_APP_SETTINGS.CONVERSATION_MODE.SINGLE'
                  )
                }}
              </option>
              <option value="multi">
                {{
                  $t(
                    'INBOX_MGMT.WAHA.CHATWOOT_APP_SETTINGS.CONVERSATION_MODE.MULTI'
                  )
                }}
              </option>
            </select>
          </div>

          <!-- Language -->
          <div>
            <label class="block text-sm font-medium text-n-slate-12 mb-1">
              {{ $t('INBOX_MGMT.WAHA.CHATWOOT_APP_SETTINGS.LANGUAGE.LABEL') }}
            </label>
            <select
              v-model="chatwootAppSettings.language"
              class="w-full max-w-xs px-3 !pr-9 bg-[right_0.65rem_center] py-2 text-sm border border-n-weak rounded-lg bg-white dark:bg-n-solid-1 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-woot-500"
            >
              <option value="en">
                {{
                  $t(
                    'INBOX_MGMT.WAHA.CHATWOOT_APP_SETTINGS.LANGUAGE.OPTIONS.en'
                  )
                }}
              </option>
              <option value="pt-br">
                {{
                  $t(
                    'INBOX_MGMT.WAHA.CHATWOOT_APP_SETTINGS.LANGUAGE.OPTIONS.pt-br'
                  )
                }}
              </option>
              <option value="es">
                {{
                  $t(
                    'INBOX_MGMT.WAHA.CHATWOOT_APP_SETTINGS.LANGUAGE.OPTIONS.es'
                  )
                }}
              </option>
              <option value="fr">
                {{
                  $t(
                    'INBOX_MGMT.WAHA.CHATWOOT_APP_SETTINGS.LANGUAGE.OPTIONS.fr'
                  )
                }}
              </option>
              <option value="de">
                {{
                  $t(
                    'INBOX_MGMT.WAHA.CHATWOOT_APP_SETTINGS.LANGUAGE.OPTIONS.de'
                  )
                }}
              </option>
              <option value="it">
                {{
                  $t(
                    'INBOX_MGMT.WAHA.CHATWOOT_APP_SETTINGS.LANGUAGE.OPTIONS.it'
                  )
                }}
              </option>
              <option value="ru">
                {{
                  $t(
                    'INBOX_MGMT.WAHA.CHATWOOT_APP_SETTINGS.LANGUAGE.OPTIONS.ru'
                  )
                }}
              </option>
              <option value="zh">
                {{
                  $t(
                    'INBOX_MGMT.WAHA.CHATWOOT_APP_SETTINGS.LANGUAGE.OPTIONS.zh'
                  )
                }}
              </option>
              <option value="ja">
                {{
                  $t(
                    'INBOX_MGMT.WAHA.CHATWOOT_APP_SETTINGS.LANGUAGE.OPTIONS.ja'
                  )
                }}
              </option>
              <option value="ko">
                {{
                  $t(
                    'INBOX_MGMT.WAHA.CHATWOOT_APP_SETTINGS.LANGUAGE.OPTIONS.ko'
                  )
                }}
              </option>
              <option value="ar">
                {{
                  $t(
                    'INBOX_MGMT.WAHA.CHATWOOT_APP_SETTINGS.LANGUAGE.OPTIONS.ar'
                  )
                }}
              </option>
            </select>
          </div>

          <!-- Checkboxes -->
          <div class="space-y-2">
            <label class="flex items-center gap-2 cursor-pointer">
              <input
                v-model="chatwootAppSettings.mark_messages_read"
                type="checkbox"
                class="w-4 h-4 text-woot-500 rounded border-n-weak"
              />
              <span class="text-sm text-n-slate-12">{{
                $t('INBOX_MGMT.WAHA.CHATWOOT_APP_SETTINGS.MARK_MESSAGES_READ')
              }}</span>
            </label>
            <label class="flex items-center gap-2 cursor-pointer">
              <input
                v-model="chatwootAppSettings.add_agent_name"
                type="checkbox"
                class="w-4 h-4 text-woot-500 rounded border-n-weak"
              />
              <span class="text-sm text-n-slate-12">{{
                $t('INBOX_MGMT.WAHA.CHATWOOT_APP_SETTINGS.ADD_AGENT_NAME')
              }}</span>
            </label>
          </div>

          <!-- Skip Chats -->
          <div>
            <p class="text-sm font-medium text-n-slate-12 mb-2">
              {{ $t('INBOX_MGMT.WAHA.CHATWOOT_APP_SETTINGS.SKIP_CHATS.LABEL') }}
            </p>
            <div class="space-y-2">
              <label class="flex items-center gap-2 cursor-pointer">
                <input
                  v-model="chatwootAppSettings.skip_chats.groups"
                  type="checkbox"
                  class="w-4 h-4 text-woot-500 rounded border-n-weak"
                />
                <span class="text-sm text-n-slate-11">{{
                  $t('INBOX_MGMT.WAHA.CHATWOOT_APP_SETTINGS.SKIP_CHATS.GROUPS')
                }}</span>
              </label>
              <label class="flex items-center gap-2 cursor-pointer">
                <input
                  v-model="chatwootAppSettings.skip_chats.status"
                  type="checkbox"
                  class="w-4 h-4 text-woot-500 rounded border-n-weak"
                />
                <span class="text-sm text-n-slate-11">{{
                  $t('INBOX_MGMT.WAHA.CHATWOOT_APP_SETTINGS.SKIP_CHATS.STATUS')
                }}</span>
              </label>
              <label class="flex items-center gap-2 cursor-pointer">
                <input
                  v-model="chatwootAppSettings.skip_chats.channels"
                  type="checkbox"
                  class="w-4 h-4 text-woot-500 rounded border-n-weak"
                />
                <span class="text-sm text-n-slate-11">{{
                  $t(
                    'INBOX_MGMT.WAHA.CHATWOOT_APP_SETTINGS.SKIP_CHATS.CHANNELS'
                  )
                }}</span>
              </label>
            </div>
          </div>

          <!-- Save -->
          <NextButton
            :is-loading="isSavingChatwootApp"
            @click="saveChatwootAppSettings"
          >
            {{ $t('INBOX_MGMT.WAHA.CHATWOOT_APP_SETTINGS.SAVE') }}
          </NextButton>
        </div>
      </SettingsSection>

      <!-- Session Settings -->
      <SettingsSection
        :title="$t('INBOX_MGMT.WAHA.SESSION_SETTINGS.TITLE')"
        :sub-title="$t('INBOX_MGMT.WAHA.SESSION_SETTINGS.SUBTITLE')"
      >
        <div class="space-y-4">
          <!-- Presence -->
          <label class="flex items-center gap-2 cursor-pointer">
            <input
              v-model="sessionSettings.presence_auto_online"
              type="checkbox"
              class="w-4 h-4 text-woot-500 rounded border-n-weak"
            />
            <span class="text-sm text-n-slate-12">{{
              $t('INBOX_MGMT.WAHA.SESSION_SETTINGS.PRESENCE_AUTO_ONLINE')
            }}</span>
          </label>

          <!-- Ignore -->
          <div>
            <p class="text-sm font-medium text-n-slate-12 mb-2">
              {{ $t('INBOX_MGMT.WAHA.SESSION_SETTINGS.IGNORE.LABEL') }}
            </p>
            <div class="space-y-2">
              <label class="flex items-center gap-2 cursor-pointer">
                <input
                  v-model="sessionSettings.ignore.groups"
                  type="checkbox"
                  class="w-4 h-4 text-woot-500 rounded border-n-weak"
                />
                <span class="text-sm text-n-slate-11">{{
                  $t('INBOX_MGMT.WAHA.SESSION_SETTINGS.IGNORE.GROUPS')
                }}</span>
              </label>
              <label class="flex items-center gap-2 cursor-pointer">
                <input
                  v-model="sessionSettings.ignore.status"
                  type="checkbox"
                  class="w-4 h-4 text-woot-500 rounded border-n-weak"
                />
                <span class="text-sm text-n-slate-11">{{
                  $t('INBOX_MGMT.WAHA.SESSION_SETTINGS.IGNORE.STATUS')
                }}</span>
              </label>
              <label class="flex items-center gap-2 cursor-pointer">
                <input
                  v-model="sessionSettings.ignore.channels"
                  type="checkbox"
                  class="w-4 h-4 text-woot-500 rounded border-n-weak"
                />
                <span class="text-sm text-n-slate-11">{{
                  $t('INBOX_MGMT.WAHA.SESSION_SETTINGS.IGNORE.CHANNELS')
                }}</span>
              </label>
              <label class="flex items-center gap-2 cursor-pointer">
                <input
                  v-model="sessionSettings.ignore.broadcast"
                  type="checkbox"
                  class="w-4 h-4 text-woot-500 rounded border-n-weak"
                />
                <span class="text-sm text-n-slate-11">{{
                  $t('INBOX_MGMT.WAHA.SESSION_SETTINGS.IGNORE.BROADCAST')
                }}</span>
              </label>
            </div>
          </div>

          <!-- Proxy -->
          <div>
            <label class="flex items-center gap-2 cursor-pointer mb-3">
              <input
                v-model="sessionSettings.proxy.enabled"
                type="checkbox"
                class="w-4 h-4 text-woot-500 rounded border-n-weak"
              />
              <span class="text-sm font-medium text-n-slate-12">{{
                $t('INBOX_MGMT.WAHA.SESSION_SETTINGS.PROXY.ENABLED')
              }}</span>
            </label>
            <div v-if="sessionSettings.proxy.enabled" class="space-y-2 ml-6">
              <input
                v-model="sessionSettings.proxy.server"
                type="text"
                :placeholder="
                  $t('INBOX_MGMT.WAHA.SESSION_SETTINGS.PROXY.SERVER')
                "
                class="w-full max-w-sm px-3 py-2 text-sm border border-n-weak rounded-lg focus:outline-none focus:ring-2 focus:ring-woot-500 bg-white dark:bg-n-solid-1 text-n-slate-12"
              />
              <input
                v-model="sessionSettings.proxy.username"
                type="text"
                :placeholder="
                  $t('INBOX_MGMT.WAHA.SESSION_SETTINGS.PROXY.USERNAME')
                "
                class="w-full max-w-sm px-3 py-2 text-sm border border-n-weak rounded-lg focus:outline-none focus:ring-2 focus:ring-woot-500 bg-white dark:bg-n-solid-1 text-n-slate-12"
              />
              <input
                v-model="sessionSettings.proxy.password"
                type="password"
                :placeholder="
                  $t('INBOX_MGMT.WAHA.SESSION_SETTINGS.PROXY.PASSWORD')
                "
                class="w-full max-w-sm px-3 py-2 text-sm border border-n-weak rounded-lg focus:outline-none focus:ring-2 focus:ring-woot-500 bg-white dark:bg-n-solid-1 text-n-slate-12"
              />
            </div>
          </div>

          <!-- NoWeb Store -->
          <div>
            <label class="flex items-center gap-2 cursor-pointer mb-2">
              <input
                v-model="sessionSettings.noweb_store.enabled"
                type="checkbox"
                class="w-4 h-4 text-woot-500 rounded border-n-weak"
              />
              <span class="text-sm font-medium text-n-slate-12">{{
                $t('INBOX_MGMT.WAHA.SESSION_SETTINGS.NOWEB_STORE.ENABLED')
              }}</span>
            </label>
            <label
              v-if="sessionSettings.noweb_store.enabled"
              class="flex items-center gap-2 cursor-pointer ml-6"
            >
              <input
                v-model="sessionSettings.noweb_store.full_sync"
                type="checkbox"
                class="w-4 h-4 text-woot-500 rounded border-n-weak"
              />
              <span class="text-sm text-n-slate-11">{{
                $t('INBOX_MGMT.WAHA.SESSION_SETTINGS.NOWEB_STORE.FULL_SYNC')
              }}</span>
            </label>
          </div>

          <!-- Save -->
          <NextButton
            :is-loading="isSavingSession"
            @click="saveSessionSettings"
          >
            {{ $t('INBOX_MGMT.WAHA.SESSION_SETTINGS.SAVE') }}
          </NextButton>
        </div>
      </SettingsSection>
    </template>
  </div>
</template>
